import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Clapperboard, Download, Trash2, Loader2, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;
const LANGS = [["en", "English"], ["fr", "Français"], ["sw", "Kiswahili"]];

export function AiVideoAdsPanel() {
  const [jobs, setJobs] = useState([]);
  const [hint, setHint] = useState("");
  const [lang, setLang] = useState("en");
  const [fmt, setFmt] = useState("vertical");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/adgen/jobs").then(({ data }) => { setJobs(data.jobs); setHint(data.cost_hint); }).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!jobs.some((j) => j.status === "queued" || j.status === "generating")) return;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [jobs]);

  const generate = async () => {
    setBusy(true);
    try {
      await api.post("/adgen/jobs", { language: lang, format: fmt });
      toast.success("Generating — real-life clip + product clip + voiceover. Usually 3–6 minutes.");
      load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail) || "Could not start"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => { await api.delete(`/adgen/jobs/${id}`).catch(() => {}); load(); };

  return (
    <div className="mt-5 rounded-lg border border-[#34C759]/30 bg-[#34C759]/5 p-4" data-testid="ai-video-ads-panel">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#34C759] font-bold"><Clapperboard className="h-3.5 w-3.5" /> AI real-life video ads</div>
      <p className="mt-1 text-xs text-neutral-400">A real-looking seller filming her goods, then a cinematic product shot, with a VISMARKS voiceover and end card inviting sellers to upload. {hint}</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <select value={lang} onChange={(e) => setLang(e.target.value)} data-testid="ai-ad-language" className="h-9 rounded-full bg-[#0A0A0A] border border-[#27272A] px-3 text-sm">
          {LANGS.map(([c, l]) => <option key={c} value={c}>{l}</option>)}
        </select>
        <select value={fmt} onChange={(e) => setFmt(e.target.value)} data-testid="ai-ad-format" className="h-9 rounded-full bg-[#0A0A0A] border border-[#27272A] px-3 text-sm">
          <option value="vertical">Vertical 9:16 (TikTok / Reels / Status)</option>
          <option value="square">Square 1:1 (Facebook / Instagram feed)</option>
        </select>
        <Button onClick={generate} disabled={busy} data-testid="ai-ad-generate" className="rounded-full bg-[#34C759] hover:bg-[#2fb350] text-black font-semibold">
          {busy ? "Starting…" : "Generate ad"}
        </Button>
      </div>
      {jobs.length > 0 && (
        <ul className="mt-4 space-y-2" data-testid="ai-ad-jobs">
          {jobs.map((j) => (
            <li key={j.id} className="rounded-lg border border-[#27272A] bg-[#0A0A0A] p-3 flex flex-wrap items-center gap-3" data-testid={`ai-ad-job-${j.id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold">{LANGS.find(([c]) => c === j.language)?.[1]} · {j.format === "vertical" ? "9:16" : "1:1"}</div>
                <div className="text-xs text-neutral-500 flex items-center gap-1.5">
                  {(j.status === "queued" || j.status === "generating") && <Loader2 className="h-3 w-3 animate-spin" />}
                  {j.status === "failed" && <AlertTriangle className="h-3 w-3 text-[#FF3B30]" />}
                  <span data-testid={`ai-ad-status-${j.id}`}>{j.status === "failed" ? j.error : j.step}</span>
                  {j.duration ? <span>· {j.duration}s · {(j.size / 1e6).toFixed(1)} MB</span> : null}
                </div>
              </div>
              {j.url && (
                <>
                  <video src={`${API}${j.url}`} controls preload="metadata" className={`rounded-md bg-black ${j.format === "vertical" ? "h-40 w-[90px]" : "h-28 w-28"}`} data-testid={`ai-ad-video-${j.id}`} />
                  <a href={`${API}${j.url}`} download target="_blank" rel="noopener noreferrer" data-testid={`ai-ad-download-${j.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-[#34C759] border border-[#34C759]/40 rounded-full px-3 py-1 hover:bg-[#34C759]/10">
                    <Download className="h-3 w-3" /> Download
                  </a>
                </>
              )}
              <button onClick={() => remove(j.id)} aria-label="Delete" data-testid={`ai-ad-delete-${j.id}`} className="p-1.5 rounded-md text-neutral-500 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10"><Trash2 className="h-4 w-4" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
